// @seventide/types — gifts module DTOs (FR-4.x, FR-9.x, R-5.5).
// Owned by the gifts module agent (see docs/ARCHITECTURE.md §6).
// Labels here are UI vocabulary only: rule figures (limits, rates, bands) always come from the rule set /
// @seventide/content (FR-12.2), never from this file.
import { z } from 'zod';
import { Id, IsoDate, IsoDateTime, Pence, TaxYear } from './common';
import { Stage } from './enums';

// ---------------------------------------------------------------- enums

export const GIFT_ASSET_TYPES = ['cash', 'shares', 'property', 'chattels', 'other'] as const;
export const GiftAssetType = z.enum(GIFT_ASSET_TYPES);
export type GiftAssetType = z.infer<typeof GiftAssetType>;
export const GIFT_ASSET_TYPE_LABELS: Record<GiftAssetType, string> = {
  cash: 'Cash or bank transfer',
  shares: 'Shares or investments',
  property: 'Property or land',
  chattels: 'Possessions (jewellery, car, furniture)',
  other: 'Something else',
};

/** What kind of recipient (drives the spouse / charity / political party / trust rules, FR-4.1). */
export const RECIPIENT_KINDS = ['person', 'charity', 'political_party', 'trust'] as const;
export const RecipientKind = z.enum(RECIPIENT_KINDS);
export type RecipientKind = z.infer<typeof RecipientKind>;
export const RECIPIENT_KIND_LABELS: Record<RecipientKind, string> = {
  person: 'A person',
  charity: 'A UK-registered charity',
  political_party: 'A political party',
  trust: 'A trust',
};

/** Recipient's relationship to the Principal (the giver). */
export const RECIPIENT_RELATIONSHIPS = [
  'spouse',
  'child',
  'grandchild',
  'great_grandchild',
  'child_in_law',
  'sibling',
  'niece_nephew',
  'other_relative',
  'friend',
  'carer',
  'charity',
  'political_party',
  'trust',
  'other',
] as const;
export const RecipientRelationship = z.enum(RECIPIENT_RELATIONSHIPS);
export type RecipientRelationship = z.infer<typeof RecipientRelationship>;
export const RECIPIENT_RELATIONSHIP_LABELS: Record<RecipientRelationship, string> = {
  spouse: 'Husband, wife or civil partner',
  child: 'Son or daughter',
  grandchild: 'Grandchild',
  great_grandchild: 'Great-grandchild',
  child_in_law: 'Son- or daughter-in-law',
  sibling: 'Brother or sister',
  niece_nephew: 'Niece or nephew',
  other_relative: 'Other relative',
  friend: 'Friend',
  carer: 'Carer',
  charity: 'Charity',
  political_party: 'Political party',
  trust: 'Trust',
  other: 'Someone else',
};

/** Giver's relationship to the person marrying (FR-4.4 (3) wedding limits). */
export const WEDDING_RELATIONSHIPS = ['parent', 'grandparent', 'other'] as const;
export const WeddingRelationship = z.enum(WEDDING_RELATIONSHIPS);
export type WeddingRelationship = z.infer<typeof WeddingRelationship>;
export const WEDDING_RELATIONSHIP_LABELS: Record<WeddingRelationship, string> = {
  parent: 'A parent of the person marrying',
  grandparent: 'A grandparent or great-grandparent of the person marrying',
  other: 'Anyone else',
};

/** Default wedding relationship from the recipient relationship. */
export function defaultWeddingRelationship(rel: RecipientRelationship | null | undefined): WeddingRelationship {
  if (rel === 'child') return 'parent';
  if (rel === 'grandchild' || rel === 'great_grandchild') return 'grandparent';
  return 'other';
}

/**
 * FR-4.3 attorney gift basis (OPG guidance, content snippet `opg-attorney-gift-guidance`).
 * `none_apply` is recorded honestly and flagged high risk (an attorney gift outside the customary limits).
 */
export const ATTORNEY_BASES = [
  'customary_occasion',
  'charity',
  'court_authorised',
  'principal_made',
  'none_apply',
] as const;
export const AttorneyBasis = z.enum(ATTORNEY_BASES);
export type AttorneyBasis = z.infer<typeof AttorneyBasis>;
export const ATTORNEY_BASIS_LABELS: Record<AttorneyBasis, string> = {
  customary_occasion: 'A customary occasion, to someone connected with them',
  charity: 'A charity they supported or might have supported',
  court_authorised: 'The Court of Protection approved this gift',
  principal_made: 'They made this gift themselves; I am only recording it',
  none_apply: 'None of these apply',
};
/** True when the basis keeps the gift within OPG customary-gift conditions (FR-9.3 input). */
export function attorneyBasisWithinLimits(b: AttorneyBasis | null | undefined): boolean | null {
  if (!b) return null;
  return b !== 'none_apply';
}

export const GIFT_FREQUENCIES = ['weekly', 'monthly', 'quarterly', 'annually'] as const;
export const GiftFrequency = z.enum(GIFT_FREQUENCIES);
export type GiftFrequency = z.infer<typeof GiftFrequency>;
export const GIFT_FREQUENCY_LABELS: Record<GiftFrequency, string> = {
  weekly: 'Every week',
  monthly: 'Every month',
  quarterly: 'Every 3 months',
  annually: 'Every year',
};

export const GIFT_STATUSES = ['expected', 'confirmed', 'cancelled'] as const;
export const GiftStatus = z.enum(GIFT_STATUSES);
export type GiftStatus = z.infer<typeof GiftStatus>;
export const GIFT_STATUS_LABELS: Record<GiftStatus, string> = {
  expected: 'Awaiting confirmation',
  confirmed: 'Confirmed',
  cancelled: 'Skipped',
};

/** Exemptions applied by the rules engine (`allocateGiftExemptions`). */
export const GIFT_EXEMPTIONS = [
  'spouse',
  'charity',
  'wedding',
  'small',
  'normal_expenditure',
  'annual_current',
  'annual_carried_forward',
  'pet',
  'clt',
] as const;
export const GiftExemption = z.enum(GIFT_EXEMPTIONS);
export type GiftExemption = z.infer<typeof GiftExemption>;
export const GIFT_EXEMPTION_LABELS: Record<GiftExemption, string> = {
  spouse: 'Spouse or civil partner',
  charity: 'Charity or political party',
  wedding: 'Wedding gift',
  small: 'Small gift',
  normal_expenditure: 'From income',
  annual_current: 'Annual exemption',
  annual_carried_forward: 'Last year’s annual exemption',
  pet: 'May become taxable (PET)',
  clt: 'Gift into trust (CLT)',
};
/** Canonical explanation id per exemption (ARCHITECTURE §7) for `<InfoButton ruleId>`. */
export const GIFT_EXEMPTION_RULE_IDS: Record<GiftExemption, string> = {
  spouse: 'iht-spouse-exemption',
  charity: 'iht-charity-exemption',
  wedding: 'iht-wedding-gifts',
  small: 'iht-small-gifts',
  normal_expenditure: 'iht-normal-expenditure-out-of-income',
  annual_current: 'iht-annual-exemption',
  annual_carried_forward: 'iht-annual-exemption-carry-forward',
  pet: 'iht-pet',
  clt: 'iht-clt',
};

export const DEPRIVATION_RISK_LEVELS = ['low', 'consider', 'high'] as const;
export const DeprivationRiskLevel = z.enum(DEPRIVATION_RISK_LEVELS);
export type DeprivationRiskLevel = z.infer<typeof DeprivationRiskLevel>;
export const DEPRIVATION_RISK_LABELS: Record<DeprivationRiskLevel, string> = {
  low: 'Lower risk',
  consider: 'Worth considering',
  high: 'Higher risk',
};

/** Why the FR-9.1 pre-save warning is shown. */
export const DEPRIVATION_GUARD_REASONS = ['care_stage', 'progressive_condition', 'recent_care_assessment'] as const;
export const DeprivationGuardReason = z.enum(DEPRIVATION_GUARD_REASONS);
export type DeprivationGuardReason = z.infer<typeof DeprivationGuardReason>;
export const DEPRIVATION_GUARD_REASON_LABELS: Record<DeprivationGuardReason, string> = {
  care_stage: 'They are receiving or about to need care',
  progressive_condition: 'Their profile records a diagnosed progressive condition',
  recent_care_assessment: 'They have had a care assessment recently',
};

/** IHT403 income and expenditure rows (same keys as @seventide/rules). */
export const IHT403_ROWS = [
  'salary_pension',
  'interest',
  'investments',
  'rents',
  'other_income',
  'mortgage',
  'insurance',
  'household_bills',
  'council_tax',
  'travel',
  'entertainment',
  'holidays',
  'nursing_home_fees',
  'other_expenditure',
] as const;
export const Iht403RowKey = z.enum(IHT403_ROWS);
export type Iht403RowKey = z.infer<typeof Iht403RowKey>;

// ---------------------------------------------------------------- shared shapes

/** One line of a rules-engine explanation trail (`{ ruleId, text, amountPence? }`). */
export const GiftExplanation = z.object({
  ruleId: z.string(),
  text: z.string(),
  amountPence: z.number().int().optional(),
});
export type GiftExplanation = z.infer<typeof GiftExplanation>;

/** FR-9.1 contemporaneous statement (stored encrypted with the gift). */
export const ContemporaneousStatement = z.object({
  /** The Principal's health and care situation when the gift was made or planned. */
  situation: z.string().trim().min(1, 'Describe their health and care situation at the time').max(4000),
  /** Whether care, a care assessment or a diagnosis had been mentioned. */
  careDiscussed: z.string().trim().max(4000).optional().nullable(),
  /** What the gift is for and why it is being made now. */
  purpose: z.string().trim().min(1, 'Say what the gift is for and why it is being made now').max(4000),
  /** Whether it is similar to gifts made in the past. */
  pastPattern: z.string().trim().max(4000).optional().nullable(),
  /** ISO timestamp the statement was written (set by the server). */
  recordedAt: IsoDateTime.optional().nullable(),
});
export type ContemporaneousStatement = z.infer<typeof ContemporaneousStatement>;

export const GiftAllocationLine = z.object({ exemption: GiftExemption, amountPence: Pence });
export type GiftAllocationLine = z.infer<typeof GiftAllocationLine>;

export const GiftAllocationDto = z.object({
  taxYear: TaxYear,
  allocations: z.array(GiftAllocationLine),
  exemptPence: Pence,
  chargeablePence: Pence,
  chargeableType: z.enum(['pet', 'clt']).nullable(),
  petExpiryDate: IsoDate.nullable(),
  taperStartDate: IsoDate.nullable(),
  remainsInEstate: z.boolean(),
  specialistReferral: z.boolean(),
  professionalReview: z.boolean(),
  explanations: z.array(GiftExplanation),
});
export type GiftAllocationDto = z.infer<typeof GiftAllocationDto>;

/** FR-4.7 PET clock (from rules `petStatus`). */
export const PetClockDto = z.object({
  yearsElapsed: z.number(),
  fullyExemptDate: IsoDate,
  taperStartDate: IsoDate,
  isFullyExempt: z.boolean(),
  inTaperPeriod: z.boolean(),
  /** Effective rate if death happened today (percentage points). */
  effectiveRatePct: z.number(),
  daysUntilFullyExempt: z.number().int(),
  /** Taper band label, e.g. "3 to 4 years" (null before the taper starts or after 7 years). */
  bandLabel: z.string().nullable(),
  explanations: z.array(GiftExplanation),
});
export type PetClockDto = z.infer<typeof PetClockDto>;

/** FR-9.3 risk indicator (rules `deprivationRisk`). Informational, never a legal determination. */
export const DeprivationRiskDto = z.object({
  level: DeprivationRiskLevel,
  pctOfCapital: z.number(),
  /** False when capital at the time of the gift is unknown (size was not considered). */
  capitalKnown: z.boolean(),
  capitalAtGiftPence: Pence.nullable(),
  reasons: z.array(z.string()),
  specialistReferral: z.boolean(),
  explanations: z.array(GiftExplanation),
});
export type DeprivationRiskDto = z.infer<typeof DeprivationRiskDto>;

// ---------------------------------------------------------------- recipients

export const RecipientDto = z.object({
  id: Id,
  name: z.string(),
  kind: RecipientKind,
  relationship: RecipientRelationship.nullable(),
  isSpouse: z.boolean(),
  isCharity: z.boolean(),
  isPoliticalParty: z.boolean(),
  isTrust: z.boolean(),
  charityNumber: z.string().nullable(),
  archived: z.boolean(),
  giftCount: z.number().int(),
  totalGivenPence: Pence,
});
export type RecipientDto = z.infer<typeof RecipientDto>;

export const CreateRecipientRequest = z.object({
  /** Optional client-generated id (offline sync, ADR 0004): returns the existing record if it exists. */
  id: Id.optional(),
  name: z.string().trim().min(1, 'Enter the recipient’s name').max(120),
  kind: RecipientKind.default('person'),
  relationship: RecipientRelationship.nullable().optional(),
  isSpouse: z.boolean().default(false),
  charityNumber: z
    .string()
    .trim()
    .max(20)
    .regex(/^[A-Za-z0-9 -]*$/, 'Use letters and numbers only')
    .nullable()
    .optional(),
});
export type CreateRecipientRequest = z.input<typeof CreateRecipientRequest>;

export const UpdateRecipientRequest = z.object({
  name: z.string().trim().min(1, 'Enter the recipient’s name').max(120).optional(),
  kind: RecipientKind.optional(),
  relationship: RecipientRelationship.nullable().optional(),
  isSpouse: z.boolean().optional(),
  charityNumber: z
    .string()
    .trim()
    .max(20)
    .regex(/^[A-Za-z0-9 -]*$/, 'Use letters and numbers only')
    .nullable()
    .optional(),
  archived: z.boolean().optional(),
});
export type UpdateRecipientRequest = z.infer<typeof UpdateRecipientRequest>;

export const RecipientListResponse = z.object({ items: z.array(RecipientDto) });
export type RecipientListResponse = z.infer<typeof RecipientListResponse>;

// ---------------------------------------------------------------- gifts

const giftFields = {
  recipientId: Id,
  date: IsoDate,
  valuePence: Pence.positive('Enter an amount more than £0'),
  assetType: GiftAssetType.default('cash'),
  description: z.string().trim().max(500).nullable().optional(),
  /** Purpose and intent (stored encrypted). */
  note: z.string().trim().max(4000).nullable().optional(),
  reservationOfBenefit: z.boolean().default(false),
  inConsiderationOfMarriage: z.boolean().default(false),
  weddingDate: IsoDate.nullable().optional(),
  weddingRelationship: WeddingRelationship.nullable().optional(),
  fromIncome: z.boolean().default(false),
  /** Part of a regular pattern (recurring schedule or at least two years of similar gifts, FR-4.6 (a)). */
  regularPattern: z.boolean().default(false),
  /** The giver's usual standard of living was maintained (FR-4.6 (b)). */
  standardOfLivingMaintained: z.boolean().default(false),
  /** Payment evidence: a money transaction (FR-4.1). */
  transactionId: Id.nullable().optional(),
  /** FR-4.3: required when an attorney records the gift. */
  attorneyBasis: AttorneyBasis.nullable().optional(),
  /** E.g. Court of Protection order date and reference (stored encrypted). */
  attorneyBasisDetails: z.string().trim().max(2000).nullable().optional(),
  /** FR-9.1 contemporaneous statement (required when the deprivation guard applies). */
  contemporaneousStatement: ContemporaneousStatement.nullable().optional(),
  /** FR-9.3: capital at the time of the gift, if known (else taken from account balances). */
  capitalAtGiftPence: Pence.nonnegative().nullable().optional(),
};

export const CreateGiftRequest = z.object({
  /** Optional client-generated id (offline sync, ADR 0004): returns the existing gift if it exists. */
  id: Id.optional(),
  ...giftFields,
});
export type CreateGiftRequest = z.input<typeof CreateGiftRequest>;

export const UpdateGiftRequest = z.object({
  recipientId: giftFields.recipientId.optional(),
  date: giftFields.date.optional(),
  valuePence: giftFields.valuePence.optional(),
  assetType: GiftAssetType.optional(),
  description: giftFields.description,
  note: giftFields.note,
  reservationOfBenefit: z.boolean().optional(),
  inConsiderationOfMarriage: z.boolean().optional(),
  weddingDate: giftFields.weddingDate,
  weddingRelationship: giftFields.weddingRelationship,
  fromIncome: z.boolean().optional(),
  regularPattern: z.boolean().optional(),
  standardOfLivingMaintained: z.boolean().optional(),
  transactionId: giftFields.transactionId,
  attorneyBasis: giftFields.attorneyBasis,
  attorneyBasisDetails: giftFields.attorneyBasisDetails,
  contemporaneousStatement: giftFields.contemporaneousStatement,
  capitalAtGiftPence: giftFields.capitalAtGiftPence,
  /** When the edit was made on the device (offline sync, ADR 0004): last write wins, the loser is kept in history. */
  clientUpdatedAt: IsoDateTime.optional(),
});
export type UpdateGiftRequest = z.input<typeof UpdateGiftRequest>;

/** POST /gifts/preview: what the engine would do with this gift (nothing is saved). */
export const PreviewGiftRequest = z.object({
  /** When editing, the gift being replaced (so it is not counted twice). */
  giftId: Id.optional(),
  ...giftFields,
});
export type PreviewGiftRequest = z.input<typeof PreviewGiftRequest>;

/** Confirm an expected (recurring) gift, optionally adjusting it; or record the Principal's confirmation. */
export const ConfirmGiftRequest = z.object({
  date: IsoDate.optional(),
  valuePence: Pence.positive('Enter an amount more than £0').optional(),
  transactionId: Id.nullable().optional(),
  contemporaneousStatement: ContemporaneousStatement.nullable().optional(),
});
export type ConfirmGiftRequest = z.input<typeof ConfirmGiftRequest>;

export const GiftListQuery = z.object({
  taxYear: TaxYear.optional(),
  recipientId: Id.optional(),
  exemption: GiftExemption.optional(),
  risk: DeprivationRiskLevel.optional(),
  fromIncome: z.enum(['true', 'false']).optional(),
  madeByAttorney: z.enum(['true', 'false']).optional(),
  /** Free-text search over recipient name and description (done after decryption). */
  q: z.string().trim().max(100).optional(),
});
export type GiftListQuery = z.infer<typeof GiftListQuery>;

export const GiftListItem = z.object({
  id: Id,
  date: IsoDate,
  taxYear: TaxYear,
  recipientId: Id,
  recipientName: z.string(),
  recipientKind: RecipientKind,
  relationship: RecipientRelationship.nullable(),
  valuePence: Pence,
  assetType: GiftAssetType,
  description: z.string().nullable(),
  status: GiftStatus,
  fromIncome: z.boolean(),
  recurringScheduleId: Id.nullable(),
  madeByAttorney: z.boolean(),
  reservationOfBenefit: z.boolean(),
  inConsiderationOfMarriage: z.boolean(),
  /** Exemption chips (empty for gifts awaiting confirmation). */
  exemptions: z.array(GiftAllocationLine),
  exemptPence: Pence,
  chargeablePence: Pence,
  chargeableType: z.enum(['pet', 'clt']).nullable(),
  petExpiryDate: IsoDate.nullable(),
  petClock: PetClockDto.nullable(),
  deprivationRisk: DeprivationRiskLevel.nullable(),
  hasStatement: z.boolean(),
  principalConfirmed: z.boolean(),
  professionalReview: z.boolean(),
  remainsInEstate: z.boolean(),
});
export type GiftListItem = z.infer<typeof GiftListItem>;

export const GiftTaxYearGroup = z.object({
  taxYear: TaxYear,
  gifts: z.array(GiftListItem),
  totalPence: Pence,
  exemptPence: Pence,
  chargeablePence: Pence,
});
export type GiftTaxYearGroup = z.infer<typeof GiftTaxYearGroup>;

export const GiftListResponse = z.object({
  currentTaxYear: TaxYear,
  taxYears: z.array(GiftTaxYearGroup),
  awaitingConfirmation: z.array(GiftListItem),
  /** Number of confirmed gifts matching the filters. */
  total: z.number().int(),
  /** Total confirmed gifts recorded (ignoring filters) — tells the UI whether the ledger is empty. */
  totalRecorded: z.number().int(),
  ruleSetVersion: z.string(),
});
export type GiftListResponse = z.infer<typeof GiftListResponse>;

export const LinkedTransactionDto = z.object({
  id: Id,
  date: IsoDate,
  amountPence: Pence,
  direction: z.enum(['in', 'out']),
  category: z.string().nullable(),
  counterparty: z.string().nullable(),
});
export type LinkedTransactionDto = z.infer<typeof LinkedTransactionDto>;

export const GiftDetail = GiftListItem.extend({
  note: z.string().nullable(),
  weddingDate: IsoDate.nullable(),
  weddingRelationship: WeddingRelationship.nullable(),
  regularPattern: z.boolean(),
  standardOfLivingMaintained: z.boolean(),
  attorneyBasis: AttorneyBasis.nullable(),
  attorneyBasisDetails: z.string().nullable(),
  attorneyConfirmedAt: IsoDateTime.nullable(),
  madeByName: z.string().nullable(),
  principalConfirmedAt: IsoDateTime.nullable(),
  transactionId: Id.nullable(),
  /** Null when there is no linked transaction or the viewer cannot see Money. */
  transaction: LinkedTransactionDto.nullable(),
  contemporaneousStatement: ContemporaneousStatement.nullable(),
  capitalAtGiftPence: Pence.nullable(),
  stageAtGift: Stage.nullable(),
  allocation: GiftAllocationDto.nullable(),
  deprivation: DeprivationRiskDto.nullable(),
  ruleSetVersion: z.string().nullable(),
  recurringSchedule: z
    .object({ id: Id, frequency: GiftFrequency, amountPence: Pence, active: z.boolean() })
    .nullable(),
  expectedSequence: z.number().int().nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type GiftDetail = z.infer<typeof GiftDetail>;

export const DeprivationGuardInfo = z.object({
  /** FR-9.1: show the pre-save warning and require the contemporaneous statement. */
  required: z.boolean(),
  reasons: z.array(DeprivationGuardReason),
});
export type DeprivationGuardInfo = z.infer<typeof DeprivationGuardInfo>;

export const GiftPreviewResponse = z.object({
  allocation: GiftAllocationDto,
  deprivation: DeprivationRiskDto,
  guard: DeprivationGuardInfo,
  petClock: PetClockDto.nullable(),
});
export type GiftPreviewResponse = z.infer<typeof GiftPreviewResponse>;

export const GiftFormContextQuery = z.object({ transactionId: Id.optional() });
export type GiftFormContextQuery = z.infer<typeof GiftFormContextQuery>;

export const GiftFormContext = z.object({
  stage: Stage,
  principalName: z.string(),
  /** FR-4.3: the recorder is an attorney (not the Principal) → basis confirmation required. */
  attorneyBasisRequired: z.boolean(),
  deprivationGuard: DeprivationGuardInfo,
  recipients: z.array(RecipientDto),
  /** Prefill from a money transaction (`?transactionId=`), when visible to the caller. */
  transaction: LinkedTransactionDto.extend({ linkedGiftId: Id.nullable() }).nullable(),
  currentTaxYear: TaxYear,
  today: IsoDate,
});
export type GiftFormContext = z.infer<typeof GiftFormContext>;

// ---------------------------------------------------------------- recurring schedules (FR-4.2)

const scheduleFields = {
  recipientId: Id,
  amountPence: Pence.positive('Enter an amount more than £0'),
  frequency: GiftFrequency,
  startDate: IsoDate,
  endDate: IsoDate.nullable().optional(),
  description: z.string().trim().max(500).nullable().optional(),
  purpose: z.string().trim().max(4000).nullable().optional(),
  assetType: GiftAssetType.default('cash'),
  fromIncome: z.boolean().default(true),
  standardOfLivingMaintained: z.boolean().default(false),
  attorneyBasis: AttorneyBasis.nullable().optional(),
  attorneyBasisDetails: z.string().trim().max(2000).nullable().optional(),
  contemporaneousStatement: ContemporaneousStatement.nullable().optional(),
};

export const CreateScheduleRequest = z.object({ id: Id.optional(), ...scheduleFields });
export type CreateScheduleRequest = z.input<typeof CreateScheduleRequest>;

export const UpdateScheduleRequest = z.object({
  amountPence: scheduleFields.amountPence.optional(),
  frequency: GiftFrequency.optional(),
  endDate: scheduleFields.endDate,
  description: scheduleFields.description,
  purpose: scheduleFields.purpose,
  fromIncome: z.boolean().optional(),
  standardOfLivingMaintained: z.boolean().optional(),
  active: z.boolean().optional(),
});
export type UpdateScheduleRequest = z.input<typeof UpdateScheduleRequest>;

export const ScheduleDto = z.object({
  id: Id,
  recipientId: Id,
  recipientName: z.string(),
  amountPence: Pence,
  frequency: GiftFrequency,
  startDate: IsoDate,
  endDate: IsoDate.nullable(),
  description: z.string().nullable(),
  purpose: z.string().nullable(),
  assetType: GiftAssetType,
  fromIncome: z.boolean(),
  standardOfLivingMaintained: z.boolean(),
  active: z.boolean(),
  madeByAttorney: z.boolean(),
  attorneyBasis: AttorneyBasis.nullable(),
  nextDueDate: IsoDate.nullable(),
  confirmedCount: z.number().int(),
  awaitingCount: z.number().int(),
  skippedCount: z.number().int(),
  totalConfirmedPence: Pence,
});
export type ScheduleDto = z.infer<typeof ScheduleDto>;

export const ScheduleListResponse = z.object({ items: z.array(ScheduleDto) });
export type ScheduleListResponse = z.infer<typeof ScheduleListResponse>;

// ---------------------------------------------------------------- tax years (FR-4.5, FR-4.6)

export const IncomeExpenditureLine = z.object({
  row: Iht403RowKey,
  label: z.string(),
  amountPence: Pence,
  /** Figure computed from categorised transactions. */
  computedPence: Pence,
  /** True when a manual total replaces the computed figure. */
  manual: z.boolean(),
});
export type IncomeExpenditureLine = z.infer<typeof IncomeExpenditureLine>;

export const IncomeExpenditureDto = z.object({
  income: z.array(IncomeExpenditureLine),
  expenditure: z.array(IncomeExpenditureLine),
  incomePence: Pence,
  expenditurePence: Pence,
  surplusPence: Pence,
  completeness: z.enum(['complete', 'incomplete']),
  /** Tax months with no data, labelled by start month `YYYY-MM`. */
  missingMonths: z.array(z.string()),
  /** Excluded from the exempt total until confirmed (FR-4.6). */
  estimate: z.boolean(),
  confirmed: z.boolean(),
  confirmedAt: IsoDateTime.nullable(),
  manualNote: z.string().nullable(),
  capitalReceiptsExcludedPence: Pence,
  excludedPence: Pence,
  transactionCount: z.number().int(),
  explanations: z.array(GiftExplanation),
  computedAt: IsoDateTime.nullable(),
});
export type IncomeExpenditureDto = z.infer<typeof IncomeExpenditureDto>;

const datedAmount = z.object({
  giftId: Id,
  date: IsoDate,
  amountPence: Pence,
  expiryDate: IsoDate,
  recipientName: z.string(),
});

export const TaxYearSummaryDto = z.object({
  taxYear: TaxYear,
  start: IsoDate,
  end: IsoDate,
  isCurrent: z.boolean(),
  ended: z.boolean(),
  giftCount: z.number().int(),
  annualExemptionPence: Pence,
  annualExemptionUsedPence: Pence,
  annualExemptionRemainingPence: Pence,
  carryForwardInPence: Pence,
  carryForwardUsedPence: Pence,
  carryForwardLapsedPence: Pence,
  carryForwardOutPence: Pence,
  smallGiftsByRecipient: z.array(
    z.object({
      recipientId: Id,
      recipientName: z.string(),
      totalGiftsPence: Pence,
      eligible: z.boolean(),
      smallGiftExemptPence: Pence,
    }),
  ),
  smallGiftsExemptPence: Pence,
  weddingExemptPence: Pence,
  charityExemptPence: Pence,
  spouseExemptPence: Pence,
  incomeExemptPence: Pence,
  /** Surplus income (income minus spending, a Money figure): null when the viewer cannot see Money (SR-31). */
  surplusAvailablePence: Pence.nullable(),
  surplusRemainingPence: Pence.nullable(),
  giftsFromIncomeNotCoveredPence: Pence,
  pets: z.array(datedAmount),
  clts: z.array(datedAmount),
  reservedPence: Pence,
  totalGiftsPence: Pence,
  totalExemptPence: Pence,
  totalChargeablePence: Pence,
  explanations: z.array(GiftExplanation),
  /** IHT403 income & expenditure table; null when the viewer cannot see Money. */
  incomeExpenditure: IncomeExpenditureDto.nullable(),
  incomeExpenditureHidden: z.boolean(),
});
export type TaxYearSummaryDto = z.infer<typeof TaxYearSummaryDto>;

export const GiftSettingsDto = z.object({
  recordsStartTaxYear: TaxYear.nullable(),
  openingCarryForwardPence: Pence,
  /** The tax year the allocation actually starts from. */
  effectiveStartTaxYear: TaxYear,
});
export type GiftSettingsDto = z.infer<typeof GiftSettingsDto>;

export const UpdateGiftSettingsRequest = z.object({
  recordsStartTaxYear: TaxYear.nullable().optional(),
  openingCarryForwardPence: Pence.nonnegative().optional(),
});
export type UpdateGiftSettingsRequest = z.infer<typeof UpdateGiftSettingsRequest>;

export const TaxYearListResponse = z.object({
  currentTaxYear: TaxYear,
  ruleSetVersion: z.string(),
  years: z.array(TaxYearSummaryDto),
  settings: GiftSettingsDto,
});
export type TaxYearListResponse = z.infer<typeof TaxYearListResponse>;

export const UpdateTaxYearRequest = z.object({
  /** Manual totals per IHT403 row; `null` removes a manual total. */
  manualLines: z.record(z.string(), Pence.nonnegative().nullable()).optional(),
  manualNote: z.string().trim().max(2000).nullable().optional(),
  /** Confirm (or un-confirm) this year's figures so an incomplete year can support the exemption. */
  confirmed: z.boolean().optional(),
});
export type UpdateTaxYearRequest = z.infer<typeof UpdateTaxYearRequest>;

// ---------------------------------------------------------------- seven-year view (FR-4.7, FR-4.9)

export const SevenYearItem = z.object({
  giftId: Id,
  date: IsoDate,
  recipientName: z.string(),
  chargeablePence: Pence,
  type: z.enum(['pet', 'clt']),
  expiryDate: IsoDate,
  taperStartDate: IsoDate,
  yearsElapsed: z.number(),
  inTaperPeriod: z.boolean(),
  effectiveRatePct: z.number(),
  nrbUsedPence: Pence,
  aboveNrbPence: Pence,
});
export type SevenYearItem = z.infer<typeof SevenYearItem>;

export const SevenYearView = z.object({
  asOf: IsoDate,
  items: z.array(SevenYearItem),
  count: z.number().int(),
  totalPence: Pence,
  nextToExpire: SevenYearItem.nullable(),
  nilRateBandPence: Pence,
  nrbUsedPence: Pence,
  nrbRemainingPence: Pence,
  exceedsNrbByPence: Pence,
  reservedPence: Pence,
  explanations: z.array(GiftExplanation),
});
export type SevenYearView = z.infer<typeof SevenYearView>;

// ---------------------------------------------------------------- home summary (ARCHITECTURE §7)

export const GiftsSummary = z.union([
  z.object({ available: z.literal(false) }),
  z.object({
    available: z.literal(true),
    taxYear: TaxYear,
    annualExemptionUsedPence: Pence,
    annualExemptionRemainingPence: Pence,
    nextPetExpiry: z.object({ date: IsoDate, valuePence: Pence, recipientName: z.string() }).nullable(),
    totalWithinSevenYearsPence: Pence,
    nrbUsedPence: Pence,
    /** Extra fields (not in the §7 contract, safe to ignore). */
    awaitingConfirmationCount: z.number().int(),
    giftCount: z.number().int(),
  }),
]);
export type GiftsSummary = z.infer<typeof GiftsSummary>;

// ---------------------------------------------------------------- estate estimate (FR-4.10)

export const ESTATE_ASSET_KINDS = ['general', 'residence', 'pension'] as const;
export const EstateAssetKind = z.enum(ESTATE_ASSET_KINDS);
export type EstateAssetKind = z.infer<typeof EstateAssetKind>;

export const ExtraEstateAsset = z.object({
  description: z.string().trim().min(1, 'Describe the asset').max(200),
  valuePence: Pence.nonnegative(),
  kind: EstateAssetKind.default('general'),
});
export type ExtraEstateAsset = z.infer<typeof ExtraEstateAsset>;

export const EstateAccountOverrideDto = z.object({
  valuePence: Pence.nonnegative().nullable().optional(),
  exclude: z.boolean().optional(),
  pensionKind: z.string().max(60).nullable().optional(),
});
export type EstateAccountOverrideDto = z.infer<typeof EstateAccountOverrideDto>;

export const EstateInputsDto = z.object({
  residenceValuePence: Pence.nullable(),
  residenceAccountId: Id.nullable(),
  accountOverrides: z.record(z.string(), EstateAccountOverrideDto),
  extraAssets: z.array(ExtraEstateAsset),
  extraDebtsPence: Pence,
  funeralCostsPence: Pence,
  spouseLegacyPence: Pence,
  charityLegacyPence: Pence,
  asOfDate: IsoDate.nullable(),
});
export type EstateInputsDto = z.infer<typeof EstateInputsDto>;

export const UpdateEstateInputsRequest = z.object({
  residenceValuePence: Pence.nonnegative().nullable().optional(),
  residenceAccountId: Id.nullable().optional(),
  accountOverrides: z.record(z.string(), EstateAccountOverrideDto).optional(),
  extraAssets: z.array(ExtraEstateAsset).max(50).optional(),
  extraDebtsPence: Pence.nonnegative().optional(),
  funeralCostsPence: Pence.nonnegative().optional(),
  spouseLegacyPence: Pence.nonnegative().optional(),
  charityLegacyPence: Pence.nonnegative().optional(),
  asOfDate: IsoDate.nullable().optional(),
});
export type UpdateEstateInputsRequest = z.input<typeof UpdateEstateInputsRequest>;

export const EstateLineDto = z.object({
  key: z.string(),
  source: z.enum(['account', 'manual', 'residence']),
  accountId: Id.nullable(),
  accountType: z.string().nullable(),
  description: z.string(),
  kind: EstateAssetKind,
  valuePence: Pence,
  /** Value from the latest balance (before any override). */
  balancePence: Pence.nullable(),
  balanceAsOf: IsoDate.nullable(),
  overridden: z.boolean(),
  included: z.boolean(),
  pensionKind: z.string().nullable(),
});
export type EstateLineDto = z.infer<typeof EstateLineDto>;

export const EstateResultDto = z.object({
  asOf: IsoDate,
  grossAssetsPence: Pence,
  pensionsIncludedPence: Pence,
  pensionsExcludedPence: Pence,
  debtsPence: Pence,
  netEstatePence: Pence,
  nilRateBandPence: Pence,
  transferredNrbPence: Pence,
  failedPetsTotalPence: Pence,
  nrbUsedByPetsPence: Pence,
  nrbAvailableToEstatePence: Pence,
  petTaxPence: Pence,
  residenceValuePence: Pence,
  rnrbMaximumPence: Pence,
  rnrbTaperReductionPence: Pence,
  rnrbAvailablePence: Pence,
  exemptLegaciesPence: Pence,
  chargeableEstatePence: Pence,
  ratePct: z.number(),
  estateTaxPence: Pence,
  totalTaxPence: Pence,
  disclaimerText: z.string(),
  explanations: z.array(GiftExplanation),
});
export type EstateResultDto = z.infer<typeof EstateResultDto>;

export const EstateEstimateResponse = z.union([
  z.object({ available: z.literal(false), reason: z.enum(['money_hidden']) }),
  z.object({
    available: z.literal(true),
    asOf: IsoDate,
    ruleSetVersion: z.string(),
    inputs: EstateInputsDto,
    assets: z.array(EstateLineDto),
    debts: z.array(EstateLineDto),
    principal: z.object({
      maritalStatus: z.string().nullable(),
      transferableNrbPct: z.number(),
      transferableRnrbPct: z.number(),
      residenceToDescendants: z.boolean().nullable(),
    }),
    failedPets: z.array(z.object({ giftId: Id, date: IsoDate, chargeablePence: Pence, recipientName: z.string() })),
    reservedGiftsPence: Pence,
    pensions: z.object({ includedFrom: IsoDate, treatment: z.string(), countedOnAsOf: z.boolean() }),
    result: EstateResultDto,
  }),
]);
export type EstateEstimateResponse = z.infer<typeof EstateEstimateResponse>;

// ---------------------------------------------------------------- deprivation schedule (FR-9.2)

export const DeprivationScheduleItem = z.object({
  giftId: Id,
  date: IsoDate,
  recipientName: z.string(),
  relationship: RecipientRelationship.nullable(),
  valuePence: Pence,
  assetType: GiftAssetType,
  description: z.string().nullable(),
  madeByAttorney: z.boolean(),
  madeByName: z.string().nullable(),
  attorneyBasis: AttorneyBasis.nullable(),
  stageAtGift: Stage.nullable(),
  statement: ContemporaneousStatement.nullable(),
  risk: DeprivationRiskDto.nullable(),
});
export type DeprivationScheduleItem = z.infer<typeof DeprivationScheduleItem>;

export const DeprivationScheduleResponse = z.object({
  asOf: IsoDate,
  fromDate: IsoDate,
  items: z.array(DeprivationScheduleItem),
  totalPence: Pence,
  counts: z.object({
    high: z.number().int(),
    consider: z.number().int(),
    low: z.number().int(),
    missingStatement: z.number().int(),
  }),
  firstCareNeedDate: IsoDate.nullable(),
});
export type DeprivationScheduleResponse = z.infer<typeof DeprivationScheduleResponse>;

// ---------------------------------------------------------------- exports (FR-4.11, R-5.5, FR-9.2)

export const GIFT_EXPORT_FORMATS = ['pdf', 'csv'] as const;
export const GiftExportFormat = z.enum(GIFT_EXPORT_FORMATS);
export type GiftExportFormat = z.infer<typeof GiftExportFormat>;

export const GiftScheduleExportRequest = z.object({
  format: GiftExportFormat.default('pdf'),
  /** One tax year, or all gifts when omitted. */
  taxYear: TaxYear.optional(),
  /** Restrict to gifts within 7 years before this date (e.g. date of death). */
  sevenYearsBefore: IsoDate.optional(),
});
export type GiftScheduleExportRequest = z.input<typeof GiftScheduleExportRequest>;

export const IncomeExpenditureExportRequest = z.object({
  format: GiftExportFormat.default('pdf'),
  /** One tax year, or every year with gifts from income when omitted. */
  taxYear: TaxYear.optional(),
});
export type IncomeExpenditureExportRequest = z.input<typeof IncomeExpenditureExportRequest>;

export const EvidenceExportRequest = z.object({
  format: GiftExportFormat.default('pdf'),
  taxYear: TaxYear.optional(),
  giftIds: z.array(Id).max(500).optional(),
});
export type EvidenceExportRequest = z.input<typeof EvidenceExportRequest>;

export const DeprivationExportRequest = z.object({ format: GiftExportFormat.default('pdf') });
export type DeprivationExportRequest = z.input<typeof DeprivationExportRequest>;
